import React from "react";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { fetchUnitSessions } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TooltipProvider } from "@/components/ui/tooltip";
import { DataTable } from "@/components/ui/data-table";
import { HistorySessionDetail, type HistorySession } from "@/views/HistorySessionDetail";
import { PaymentCell } from "@/components/PaymentStatus";

interface SessionHistoryProps {
  unitId: string;
  showCosts?: boolean;
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds < 0) return "—";
  if (seconds === 0) return "0m";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function formatEnergy(wh: number | null | undefined): string {
  if (wh === null || wh === undefined) return "—";
  return `${(wh / 1000).toFixed(2)} kWh`;
}

function formatCost(cost: number | null | undefined): string {
  if (cost === undefined || cost === null) return "—";
  return `$${cost.toFixed(2)}`;
}

const baseHistoryColumns: ColumnDef<any, any>[] = [
  {
    accessorKey: "start_time",
    header: "Start",
    cell: ({ getValue }) => {
      const v = getValue();
      return <span className="font-mono text-xs">{v ? new Date(v).toLocaleString() : "—"}</span>;
    },
  },
  {
    id: "driver",
    header: "Driver",
    // Who charged: the bound customer's name, or "—" when the session was
    // anonymous/guest (no customer bound). Full detail (email, "Started by"
    // fallback label) stays in the row-click dialog.
    cell: ({ row }) =>
      row.original.customer_name || <span className="opacity-50">—</span>,
  },
  {
    accessorKey: "energy_delivered",
    header: "Energy",
    cell: ({ getValue }) => formatEnergy(getValue()),
  },
  {
    accessorKey: "duration_seconds",
    header: "Duration",
    cell: ({ getValue }) => formatDuration(getValue()),
  },
  // Billed vs Captured, plus a factual Payment status — SAME vocabulary as the
  // admin org sessions report (a session can show a Billed amount while nothing
  // was captured, e.g. a canceled/uncollected paid session; "Billed" here never
  // implies collected revenue). Columns filtered out when showCosts is false.
  {
    id: "cost",
    header: "Billed",
    cell: ({ row }) => formatCost(row.original.costs?.total_cost),
  },
  {
    id: "captured",
    header: "Captured",
    // captured_payment is the canonical captured figure (same as the org report's
    // Captured column). "Unavailable" = the payment projection failed to load;
    // "—" = the session genuinely has no payment facts.
    cell: ({ row }) =>
      row.original.payment
        ? formatCost(row.original.payment.captured_payment)
        : row.original.paymentUnavailable
        ? <span className="opacity-60">Unavailable</span>
        : "—",
  },
  {
    id: "payment",
    header: "Payment",
    cell: ({ row }) =>
      row.original.payment ? (
        <PaymentCell r={row.original.payment} showStripeLink={false} />
      ) : row.original.paymentUnavailable ? (
        <span className="opacity-60">Unavailable</span>
      ) : (
        "—"
      ),
  },
  {
    id: "subsessions",
    header: "Subsessions",
    cell: ({ row }) => row.original.subsessions_count ?? row.original.subsessions?.length ?? 0,
  },
];

// The cost-derived columns (Billed / Captured / Payment) are shown only where a
// cost is meaningful — same gate as the old single Cost column.
const COST_COLUMN_IDS = new Set(["cost", "captured", "payment"]);

const HISTORY_PAGE_SIZE = 25;

export function SessionHistory({ unitId, showCosts = true }: SessionHistoryProps) {
  const historyColumns = showCosts
    ? baseHistoryColumns
    : baseHistoryColumns.filter((c) => !COST_COLUMN_IDS.has((c as any).id));

  const queryClient = useQueryClient();
  const [selectedSession, setSelectedSession] = React.useState<HistorySession | null>(null);

  // Live current/active session — polled separately from the append-only history.
  const currentQ = useQuery({
    queryKey: ["unit", unitId, "current-session"],
    queryFn: () => fetchUnitSessions(unitId, { include_history: false, limit: 1 }),
    enabled: !!unitId,
    refetchInterval: 10_000,
  });
  const current_session = currentQ.data?.current_session;

  // Paginated history via infinite scroll (Task 25 follow-up). The server caps
  // non-admins to their tier limit (free tier = last 3) and reports when no
  // more pages exist; admins page lazily through the full history.
  const historyQ = useInfiniteQuery({
    queryKey: ["unit", unitId, "sessions", "history"],
    enabled: !!unitId,
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      fetchUnitSessions(unitId, {
        include_history: true,
        limit: HISTORY_PAGE_SIZE,
        offset: pageParam as number,
      }),
    getNextPageParam: (lastPage) => lastPage.history_next_offset ?? undefined,
  });

  const session_history = React.useMemo(
    () =>
      (historyQ.data?.pages ?? []).flatMap((p) =>
        // Tag each row with whether ITS page's payment projection failed to load,
        // so a row without `payment` can be shown as "Unavailable" (load error)
        // vs "—" (no payment facts). Preserved through row-click into the dialog.
        (p.session_history ?? []).map((s: any) => ({
          ...s,
          paymentUnavailable: p.payment_available === false,
        })),
      ),
    [historyQ.data],
  );

  // Auto-load the next page when the sentinel scrolls into view.
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = historyQ;
  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "120px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, session_history.length]);

  // When an active session ends it becomes history — refresh the list so it
  // appears at the top. Invalidating refetches the already-loaded pages by
  // their offsets (newest-first, contiguous → no duplicates), preserving how
  // far the user had scrolled.
  const hadActiveSession = React.useRef(false);
  React.useEffect(() => {
    const hasActive = !!current_session;
    if (hadActiveSession.current && !hasActive) {
      queryClient.invalidateQueries({ queryKey: ["unit", unitId, "sessions", "history"] });
    }
    hadActiveSession.current = hasActive;
  }, [current_session, queryClient, unitId]);

  if (currentQ.isLoading && historyQ.isLoading) {
    return <div className="text-sm opacity-60">Loading sessions...</div>;
  }
  if (historyQ.isError) return <div className="text-sm text-error">Failed to load sessions</div>;

  return (
    <TooltipProvider delayDuration={100}>
    <div className="space-y-4">
      {/* Current Session */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Current Session
            {current_session && <Badge variant="success">Active</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {current_session ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <div className="text-xs opacity-60">Energy</div>
                <div className="font-semibold">{formatEnergy(current_session.energy_delivered)}</div>
              </div>
              <div>
                <div className="text-xs opacity-60">Duration</div>
                <div className="font-semibold">{formatDuration(current_session.duration_seconds)}</div>
              </div>
              {showCosts && (
              <div>
                <div className="text-xs opacity-60">Cost</div>
                <div className="font-semibold">{formatCost(current_session.costs?.total_cost)}</div>
              </div>
              )}
              <div>
                <div className="text-xs opacity-60">Subsessions</div>
                <div className="font-semibold">{current_session.subsessions_count ?? current_session.subsessions?.length ?? 0}</div>
              </div>
              {current_session.start_time && (
                <div className="col-span-2">
                  <div className="text-xs opacity-60">Started</div>
                  <div className="font-mono text-xs">{new Date(current_session.start_time).toLocaleString()}</div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm opacity-60">No active session</div>
          )}
        </CardContent>
      </Card>

      {/* Session History */}
      <Card>
        <CardHeader>
          <CardTitle>Session History</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={historyColumns}
            data={session_history}
            tableClassName="text-xs"
            isLoading={historyQ.isLoading}
            emptyMessage="No session history"
            onRowClick={(row) => setSelectedSession(row as HistorySession)}
            footer={
              <div ref={sentinelRef} className="py-3 text-center text-xs opacity-60">
                {isFetchingNextPage
                  ? "Loading more…"
                  : hasNextPage
                  ? "Scroll to load more"
                  : session_history.length > 0
                  ? "End of history"
                  : null}
              </div>
            }
          />
        </CardContent>
      </Card>

      {/* Per-session detail: interval breakdown (paid vs free), tariff, user */}
      <HistorySessionDetail
        session={selectedSession}
        showCosts={showCosts}
        onClose={() => setSelectedSession(null)}
      />
    </div>
    </TooltipProvider>
  );
}
